import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import {
  FETCH_QUOTE_BY_ID,
  FETCH_QUOTE_WORK_SPLIT_BY_ID,
  FIND_ALL_QUOTES_PAGINATION,
  GENERATE_QUOTATION_PDF,
  GET_LEADS,
  GET_PARENT_QUOTE_BY_ID,
  GET_PARENT_QUOTES,
  GET_QUOTE_SUMMARY,
  GET_QUOTES,
  LIST_PROJECTS,
} from '../graphql/quotation.queries';
import {
  ACCEPT_QUOTATION,
  APPROVE_QUOTATION,
  BULK_CREATE_QUOTE,
  CANCEL_VARIATION,
  CHANGE_IS_EDITABLE,
  CREATE_QUOTATION,
  CREATE_QUOTE_PROJECT,
  CREATE_QUOTE_WORK_SPLIT,
  EDIT_QUOTATION,
  FINALIZE_QUOTE,
  GENERATE_QUOTE_PDF,
  QUOTE_MARK_AS_SENT,
  REJECT_QUOTE,
  REMOVE_PARENT_QUOTE,
  REMOVE_QUOTE,
  REMOVE_QUOTE_ATTACHMENT_BY_ID,
  REMOVE_QUOTE_EXCLUSION,
  REMOVE_QUOTE_ITEM_BY_ID,
  REVISE_QUOTATION,
  REVISION_QUOTATION,
  SAVE_WORK_ORDER,
  UPDATE_PARENT_QUOTE_STATUS,
  UPDATE_QUOTATION,
  UPDATE_QUOTE_WORK_SPLIT,
} from '../graphql/quotation.mutation';

@Injectable({ providedIn: 'root' })
export class QuotationService {
  constructor(private apollo: Apollo) { }

  // fetch all quotations
  fetchQuotations(search?: String, status?: String, startDate?: any, endDate?: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: GET_QUOTES,
        variables: {
          search,
          status,
          startDate,
          endDate
        },
        fetchPolicy: 'no-cache',
      })
      .valueChanges.pipe(map((response) => response.data.quotes));
  }
  leadsDropDown(search: String, leadStatus?: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: GET_LEADS,
        variables: {
          search,
          leadStatus
        },
        fetchPolicy: 'no-cache',
      })
      .valueChanges.pipe(map((response) => response.data.leadsDropDown));
  }

  //fetch parent quotations
  fetchParentQuotes(search: string = '', status: string = '', projectId?: number, startDate?: any, endDate?: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: GET_PARENT_QUOTES,
        variables: {
          search,
          status,
          projectId,
          startDate,
          endDate
        },
        fetchPolicy: 'no-cache',
      })
      .valueChanges.pipe(map((response) => response.data.parentQuotes));
  }

  //fetch parent quote by id
  fetchParentQuoteById(id: number): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: GET_PARENT_QUOTE_BY_ID,
        variables: {
          id: id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((response) => response.data?.parentQuote));
  }

  //create quote work split
  createQuotesWorkSplit(createQuotesWorkInputs: any[]): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: CREATE_QUOTE_WORK_SPLIT,
      variables: {
        createQuotesWorkInputs: createQuotesWorkInputs,
      },
    });
  }
  
  //create quote work split
  bulkCreateQuote(
    projectId: any,
    partnerId: any,
    quote: any,
    requirement: any,
    TRN: any,
    SOW: any,
    description: any,
    unit: any,
    price: any,
    vat: any,
    total: any,
    workOrder: any,
    startDate: any,
    endDate: any,
  ): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: BULK_CREATE_QUOTE,
      variables: {
        projectId,
        partnerId,
        quote,
        requirement,
        TRN,
        SOW,
        description,
        unit,
        price,
        vat,
        total,
        workOrder,
        startDate,
        endDate,
      },
    });
  }

  //update quote work split
  updateQuoteWorkSplit(updateQuotesWorkInputs: any[]): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: UPDATE_QUOTE_WORK_SPLIT,
      variables: {
        updateQuotesWorkInputs: updateQuotesWorkInputs,
      },
    });
  }

  // createQuote
  createQuote(
    createQuoteInput: any,
    items: any[],
  ): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: CREATE_QUOTATION,
      variables: {
        createQuoteInput,
        items
      },
    });
  }

  //finalize quote
  finalizeQuote(
    quoteId: any,
    quoteItemsInput: any[],
    quoteExclusionInput: any[],
    quoteAttachmentsInput: any[]
  ): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: FINALIZE_QUOTE,
      variables: {
        quoteId: quoteId,
        quoteItemsInput: quoteItemsInput,
        quoteExclusionInput: quoteExclusionInput,
        quoteAttachmentsInput: quoteAttachmentsInput,
      },
    });
  }

  //remove quote exclusion
  removeQuoteExclusion(id: number): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: REMOVE_QUOTE_EXCLUSION,
      variables: {
        id,
      },
    });
  }

  //update quotation
  updateQuotation(id: any, updateQuoteInput: any, items: any): Observable<any> {
    return this.apollo.use('project').mutate(
      {
        mutation: UPDATE_QUOTATION,
        variables: {
          id,
          updateQuoteInput,
          items,
        }
      }
    );
  }

  //quote summary
  fetchQuoteSummary(startDate?: any, endDate?: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: GET_QUOTE_SUMMARY,
        variables: {
          startDate,
          endDate
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((response) => response.data.getQuoteOverview));
  }

  //quote revision
  revisionQuote(id: number): Observable<any> {
    return this.apollo.use('project').mutate(
      {
        mutation: REVISION_QUOTATION,
        variables: {
        id
        }
      }
    );
  }
  acceptQuote(id: number): Observable<any> {
    return this.apollo.use('project').mutate(
      {
        mutation: ACCEPT_QUOTATION,
        variables: {
        id
        }
      }
    );
  }

  approveQuote(id: number): Observable<any> {
    return this.apollo.use('project').mutate(
      {
        mutation: APPROVE_QUOTATION,
        variables: {
        id
        }
      }
    );
  }

  //quote variation
  reviseQuoteVariation(parentQuoteId: number, quoteId: number): Observable<any> {
    return this.apollo.use('project').mutate(
      {
        mutation: REVISE_QUOTATION,
        variables: {
          parentQuoteId: parentQuoteId,
          quoteId: quoteId
        }
      }
    );
  }

  cancelVariationQuote(quoteId: number): Observable<any> {
    return this.apollo.use('project').mutate(
      {
        mutation: CANCEL_VARIATION,
        variables: {
          quoteId: quoteId
        }
      }
    );
  }

  //list projetcs
  listProjects(search: string): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: LIST_PROJECTS,
        variables: {
          search,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((response) => response.data.listProjects));
  }

  removeQuoteItemById(id: number): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: REMOVE_QUOTE_ITEM_BY_ID,
      variables: {
        id,
      },
    });
  }

  // remove invoice attachment
  removeQuoteAttachmentById(id: number): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: REMOVE_QUOTE_ATTACHMENT_BY_ID,
      variables: {
        id,
      },
    });
  }

  //enable edit quotation
  changeIsEditable(id: number, isEditable: boolean): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: CHANGE_IS_EDITABLE,
      variables: {
        id, isEditable
      }
    })
  }

  // fetch work split by id
  fetchWorkSplitById(id: number): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: FETCH_QUOTE_WORK_SPLIT_BY_ID,
        variables: {
          id: id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((response) => response.data?.findOneQuotesWork));
  }

  //fetch quotation by id
  fetchQuoteById(id: number): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: FETCH_QUOTE_BY_ID,
        variables: {
          id: id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((response) => response.data?.quote));
  }

  //generate quote pdf
  generateQuotePDF(quoteId: number): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: GENERATE_QUOTE_PDF,
      variables: {
        quoteId,
      },
    });
  }

  //edit quotation
  editQuotation(parentQuoteId: number, branchId: number): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: EDIT_QUOTATION,
      variables: {
        parentQuoteId, branchId
      }
    })
  }

  //save work order
  saveWorkOrder(quoteId: number): Observable<any> {
    const saveWorkOrder = {
      quoteId: quoteId,
    };
    return this.apollo.use('project').mutate({
      mutation: SAVE_WORK_ORDER,
      variables: {
        saveWorkOrder,
      },
    });
  }

  //save work order
  createQuoteProject(id: number): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: CREATE_QUOTE_PROJECT,
      variables: {
        id: id,
      },
    });
  }

  //remove parent quote
  removeParentQuote(id: number): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: REMOVE_PARENT_QUOTE,
      variables: {
        id,
      },
    });
  }

  //remove quote
  removeQuote(id: number): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: REMOVE_QUOTE,
      variables: {
        id,
      },
    });
  }

  //close parent quote status
  updateParentQuoteStatus(id: number, status: string): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: UPDATE_PARENT_QUOTE_STATUS,
      variables: {
        id: id,
        status: status
      },
    });
  }

  //quote mark as sent
  markAsSentQuote(id: number): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: QUOTE_MARK_AS_SENT,
      variables: {
        id
      },
    });
  }

  rejectQuote(id: number): Observable<any> {
    return this.apollo.use('project').mutate({
      mutation: REJECT_QUOTE,
      variables: {
        id
      },
    });
  }

  generateQuotationPdf(quoteId: number): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: GENERATE_QUOTATION_PDF,
        variables: {
          quoteId: quoteId,
        },
      })
      .valueChanges.pipe(map((response) => response.data?.getQuotePDF));
  }

  findAllQuotessWithPagination(
        input: any,
      ): Observable<any> {
        return this.apollo
          .use('project')
          .watchQuery<any>({
            query: FIND_ALL_QUOTES_PAGINATION,
            variables: {
              input
            },
            fetchPolicy: 'network-only',
          })
          .valueChanges.pipe(
            map((result) => result.data?.findAllQuotesWithPagination || [])
          );
      }
}
